import { FC, useEffect, useState } from 'react';
import { Backdrop, Button, Modal } from '@mui/material';
import SimpleInput from 'src/components/Items/input/SimpleInput';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import NumberInput from 'src/components/Items/input/NumberInput';
import RatingInput from 'src/components/Items/input/StyledRating';
import { useActions } from 'src/hooks/useActions';
import { validateField, validateNumber } from 'src/common/utils/validation';
import { IFeedback, IFeedbackFetch } from 'src/models/IHost';

interface IProps {
  open: boolean;
  handleClose: () => void;
  review: IFeedback | IFeedbackFetch;
  isEdit?: boolean;
}

export type IErrorsFeedbackData = {
  order_id?: string | undefined;
  description?: string | undefined;
  rates?: string | undefined;
};

const ReviewModal: FC<IProps> = ({ open, handleClose, review, isEdit }) => {
  const { updateReview, addReview } = useActions();

  const [feedback, setFeedback] = useState({} as IFeedback);
  const [disable, setDisable] = useState(true);
  const [check, setCheck] = useState(false);
  const [errors, setErrors] = useState<IErrorsFeedbackData>({});

  useEffect(() => {
    setFeedback(review);
  }, [review]);

  useEffect(() => {
    if (JSON.stringify(review) === JSON.stringify(feedback)) {
      setDisable(true);
    } else setDisable(false);
  }, [feedback]);

  const handleInput = (name: string) => (value: string | number) => {
    setFeedback({ ...feedback, [name]: value });
  };

  const validateInputs = (): boolean => {
    const order_id = isEdit ? '' : validateNumber(feedback.order_id);
    const description = isEdit ? '' : validateField(feedback.description);
    const rates = isEdit ? '' : validateNumber(feedback.rates);

    setErrors({
      order_id,
      description,
      rates
    });
    return [order_id, description, rates].every(el => !el);
  };

  const handleCloseModal = () => {
    setCheck(false);
    setErrors({});
    handleClose();
  };

  const handleSubmit = () => {
    setCheck(true);
    if (validateInputs()) {
      handleCloseModal();
      if (isEdit) {
        updateReview(
          {
            order_id: feedback.order_id,
            description: feedback.description,
            link: feedback.link,
            rates: feedback.rates
          } as IFeedbackFetch,
          (feedback as IFeedbackFetch).id
        );
      } else addReview(feedback);
    }
  };

  return (
    <Modal open={open} onClose={handleCloseModal} BackdropComponent={Backdrop} closeAfterTransition>
      <>
        <div className='modal modal-650'>
          <div className='modal__title'>{isEdit ? 'Edit' : 'Add'} Feedback</div>
          <div className='col-1'>
            <div className='col-2'>
              <NumberInput
                data={feedback['order_id']}
                setData={handleInput('order_id')}
                placeholder='Order ID'
                min={0}
                max={99999}
                title={'order ID'}
                check={check}
                error={errors.order_id}
              />
              <RatingInput
                value={feedback.rates}
                setValue={handleInput('rates')}
                error={errors.rates}
              />
            </div>
            <SimpleInput
              data={feedback['link']!}
              setData={handleInput('link')}
              title={'link'}
              length={300}
            />
            <SimpleInput
              data={feedback['description']!}
              setData={handleInput('description')}
              title={'description'}
              check={check}
              error={errors.description}
              length={3000}
              isHelperText={true}
              variant='outlined'
              rows={3}
              multiline
            />
          </div>
          <div className='col-2' style={{ marginTop: '30px' }}>
            <SecondTypeButton children='Cancel' onClick={handleCloseModal} />
            <Button
              variant='contained'
              onClick={handleSubmit}
              children={`${isEdit ? 'Edit' : 'Add'} Feedback`}
              disabled={isEdit ? disable : false}
            />
          </div>
        </div>
      </>
    </Modal>
  );
};
export default ReviewModal;
